import { ImageChange, SavedImage, type UpdateImageRequest } from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

const base = (projectId: string) => `/projects/${projectId}/images`;

/** The project's snapshots and backups, newest first; live updates refresh them via `projects`. */
export const useImages = (projectId: string) =>
  useQuery({
    queryKey: ["projects", projectId, "images"],
    queryFn: () => api(base(projectId), { schema: z.array(SavedImage) }),
  });

/** Image writes change servers too (restores, backups), so every project query is refreshed. */
function useImageMutation<T, R>(projectId: string, fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ["projects", projectId] }),
  });
}

export const useUpdateImage = (projectId: string) =>
  useImageMutation(projectId, ({ id, ...body }: { id: string } & UpdateImageRequest) =>
    api(`${base(projectId)}/${id}`, { method: "PATCH", body, schema: SavedImage }),
  );

export const useConvertImage = (projectId: string) =>
  useImageMutation(projectId, (id: string) =>
    api(`${base(projectId)}/${id}/actions/convert`, { method: "POST", schema: SavedImage }),
  );

export const useImageProtection = (projectId: string) =>
  useImageMutation(projectId, ({ id, protect }: { id: string; protect: boolean }) =>
    api(`${base(projectId)}/${id}/actions/protection`, {
      method: "POST",
      body: { delete: protect },
      schema: ImageChange,
    }),
  );

export const useDeleteImage = (projectId: string) =>
  useImageMutation(projectId, (id: string) =>
    api(`${base(projectId)}/${id}`, { method: "DELETE" }),
  );
